import React, { useEffect, useState } from 'react';
import './item-type.styles.scss';
import ToggleInput from '@/components/SHARED/toggle-input/toggle-input.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { useSelector } from 'react-redux';
import { membershipRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useEffectiveMembership } from '@/utils/useEffectiveMembership';

const ManageItemType = ({ 
    type, 
    saveData, 
    formData,
    setAlertMessage,
    disabled
}) => {

    const membership = useEffectiveMembership();
    const screenSize = useSelector(state => state.contentData.screenSize);
    const [ blocked, setBlocked ] = useState(
        membershipRestrictions[membership.membership]
        .blocked
    )
    const [ showUpgradeModal, setShowUpgradeModal ] = useState(false);
    const [ infoText ] = useState({
        "inventory": `<h3>Inventory Items</h3><p>Can be either earned or purchased in the Marketplace, ` +
        `and will be stored in the player's inventory to be used as a game ` +
        `mechanic at a later date. These items can be sold or traded to other players.</p>`,
        "realworld": `<h3>Real World Items</h3><p>Must be purchased in the Marketplace, and ` +
        `represents a real world item for you to distribute. Players will be able to see their ` +
        `purchase history, but these items will not be kept in their inventory.`,
        "collectible": `<h3>Collectible Items</h3><p>Can be either earned or purchased ` +
        `in the Marketplace, ` +
        `and will be stored in the player's inventory. These items can be sold or ` +
        `traded to other players. ` +
        `Collectible items can be used to create a collection list, which players ` + 
        `can complete for rewards.</p>`,
        "lottery": `<h3>Lottery Tickets</h3><p>Can be either earned or purchased in ` + 
        `the Marketplace, ` +
        `and count as one chance of winning a lottery. A winner will be chosen from ` +
        `those who own these types of tickets. ` +
        `These can be sold or traded to other players.</p>`,
        "contribution": `<h3>Player-choice Contributions</h3><p>Purchased in the ` +
        `Marketplace, this is an opportunity for a player to contribute currency to a collection. ` +
        `You can set a goal for the players to achieve. Players will be able to see ` +
        `their purchase history, but these items will not be kept in their inventory.</p>`
    })
    
    useEffect(() => {
        if (formData.opt_itemType && !formData.ts_created) {
            saveData('opt_visible', true);
            saveData('opt_available', true);
        }
    }, [formData.opt_itemType])

    return (
        <div className={`item-type ${screenSize}`}>
            <div className='g-list-item form-section'>
                <h3>Item Type<span className='required'>*</span></h3>
                <hr />
                <p>Choose what type of item this will be.</p>
                <div className="item-type-content">
                    <div className={`toggle-with-text`}>
                        <ToggleInput 
                            contentKey="opt_itemType" 
                            checked={formData.opt_itemType === 'inventory'} 
                            saveData={saveData}
                            value='inventory' 
                            disabled={disabled}
                        />
                        <div className='toggle-text'>
                            <div className='image'>
                                <ImageContainer 
                                    src='/images/icons/item_inventory2.png' 
                                    alt='inventory icon' 
                                    className='label-image' 
                                />
                            </div>
                            <div className='text'>
                                <h3 style={{'color': 'var(--item-inventory)'}}>Inventory Item</h3>
                            </div> 
                            <button 
                                className='g-button text-only info-button'
                                onClick={() => setAlertMessage(infoText.inventory)}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                        </div>
                    </div>
                    <div className="g-space-1"></div>
                    <div className={`toggle-with-text`}>
                        <ToggleInput 
                            contentKey="opt_itemType" 
                            checked={formData.opt_itemType === 'realworld'} 
                            saveData={saveData}
                            value='realworld' 
                            disabled={disabled}
                        />
                        <div className='toggle-text'>
                            <div className='image'>
                                <ImageContainer 
                                    src='/images/icons/item_realworld2.png' 
                                    alt='realworld icon' 
                                    className='label-image' 
                                />
                            </div>
                            <div className='text'>
                                <h3 style={{'color': 'var(--item-realworld)'}}>Real World Item</h3>
                            </div>
                            <button 
                                className='g-button text-only info-button'
                                onClick={() => setAlertMessage(infoText.realworld)}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                        </div>
                    </div>
                    <div className="g-space-1"></div>
                    <div 
                        className={`toggle-with-text`}
                        onClick={() => blocked.includes('item_collectible') ? setShowUpgradeModal(true) : null}>
                        <ToggleInput 
                            contentKey="opt_itemType" 
                            checked={formData.opt_itemType === 'collectible'} 
                            saveData={saveData}
                            value='collectible' 
                            disabled={disabled || blocked.includes('item_collectible')}
                        />
                        <div className='toggle-text'>
                            <div className='image'>
                                <ImageContainer 
                                    src='/images/icons/item_collectible2.png' 
                                    alt='collectible icon' 
                                    className='label-image' 
                                />
                            </div>
                            <div className='text'>
                                <h3 style={{'color': 'var(--item-collectible)'}}>Collectible Item</h3>
                            </div>
                            <button 
                                className='g-button text-only info-button'
                                onClick={() => setAlertMessage(infoText.collectible)}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                        </div>
                    </div>
                    <div className="g-space-1"></div>
                    <div 
                        className={`toggle-with-text`}
                        onClick={() => blocked.includes('item_lottery') ? setShowUpgradeModal(true) : null}>
                        <ToggleInput 
                            contentKey="opt_itemType" 
                            checked={formData.opt_itemType === 'lottery'} 
                            saveData={saveData}
                            value='lottery' 
                            disabled={disabled || blocked.includes('item_lottery')} />
                        <div className='toggle-text'>
                            <div className='image'>
                                <ImageContainer 
                                    src='/images/icons/item_lottery2.png' 
                                    alt='lottery icon' 
                                    className='label-image' 
                                />
                            </div>
                            <div className='text'>    
                                <h3 style={{'color': 'var(--item-lottery)'}}>Lottery Ticket</h3>
                            </div>
                            <button 
                                className='g-button text-only info-button'
                                onClick={() => setAlertMessage(infoText.lottery)}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                        </div>
                    </div>
                    <div className="g-space-1"></div>
                    <div 
                        className={`toggle-with-text`}
                        onClick={() => blocked.includes('item_contribution') ? setShowUpgradeModal(true) : null} >
                        <ToggleInput 
                            contentKey="opt_itemType" 
                            checked={formData.opt_itemType === 'contribution'} 
                            saveData={saveData}
                            value='contribution' 
                            disabled={disabled || blocked.includes('item_contribution')}
                        />
                        <div className='toggle-text'>
                            <div className='image'>
                                <ImageContainer 
                                    src='/images/icons/item_contribution2.png' 
                                    alt='contribution icon' 
                                    className='label-image' 
                                />
                            </div>
                            <div className='text'>    
                                <h3 style={{'color': 'var(--item-contribution)'}}>Contribution Item</h3>
                            </div>
                            <button 
                                className='g-button text-only info-button'
                                onClick={() => setAlertMessage(infoText.contribution)}>
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="g-space-1"></div>
            </div>
            <UpgradeRequiredModal
                show={showUpgradeModal}
                cancel={() => setShowUpgradeModal(false)}
                message="You must have a paid plan in order to use Collectibles, Lottery Tickets, and Player-choice Contributions."
                closeButton={true}
                newTab={true} />
        </div>
    )
}

export default ManageItemType